/**
 * ProtectedRoute — Guards routes that require authentication.
 * Redirects to /landing if not authenticated.
 * Shows a loading spinner while auth state is being determined.
 * issue_139: Added optional allowedRoles prop for role-based route protection.
 * issue_163: Added redirect for USER (pending) role to /onboarding.
 * issue_215: Changed unauthenticated redirect from /login to /landing.
 */

import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { UserRole } from '@flashcard/shared';
import { useAuth } from './auth.hook';
import { Spinner } from '@/components/ui/Spinner';

interface ProtectedRouteProps {
  children: ReactNode;
  /** Optional: restrict access to specific roles. If the user's role is not
   *  in this list, they will be redirected to the home page. */
  allowedRoles?: UserRole[];
}

export function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { isAuthenticated, isInitializing, user } = useAuth();
  const location = useLocation();

  // Show spinner while initializing (first page load / restoring session)
  if (isInitializing) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="xl" />
      </div>
    );
  }

  // Redirect to landing if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/landing" replace />;
  }

  // If user is pending (USER role) and trying to access a non-onboarding route,
  // redirect to /onboarding. Allow access to /onboarding, /settings, /organization/register, /organization/join
  if (user && user.role === UserRole.USER) {
    const allowedPendingPaths = ['/onboarding', '/settings', '/organization/register', '/organization/join'];
    if (!allowedPendingPaths.includes(location.pathname)) {
      return <Navigate to="/onboarding" replace />;
    }
  }

  // Role-based check: if allowedRoles is specified and user's role is not in the list,
  // redirect to the home page.
  if (allowedRoles && allowedRoles.length > 0 && user) {
    const userRole = user.role as UserRole;
    if (!allowedRoles.includes(userRole)) {
      // Redirect org profiles to their appropriate home, others to dashboard
      if (userRole === UserRole.ORGANIZATION_ADMIN) {
        return <Navigate to="/organization" replace />;
      }
      if (userRole === UserRole.ORGANIZATION_MEMBER) {
        return <Navigate to="/my-courses" replace />;
      }
      return <Navigate to="/" replace />;
    }
  }

  return <>{children}</>;
}

export default ProtectedRoute;